import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Users, History, Layers, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { GoalCard, GoalPicker } from "@/components/family/GoalCard";
import { MemberRows } from "@/components/family/MemberRows";
import { HeadToHead } from "@/components/family/HeadToHead";
import { CallCard } from "@/components/calls/CallCard";

const weekLabel = (w) => new Date(`${w}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

const Upgrade = ({ copy }) => {
  const navigate = useNavigate();
  return (
    <div className="tc-card p-6 max-w-xl" data-testid="family-week-upgrade">
      <div className="font-semibold flex items-center gap-2"><Lock className="w-4 h-4 text-tc-coach" /> Family Week comes with the Family plan</div>
      <p className="text-sm text-tc-sub mt-2">{copy}</p>
      <button data-testid="family-week-see-plans" onClick={() => navigate("/pricing")} className="mt-4 rounded-lg bg-tc-coach text-tc-bg font-semibold px-5 py-2.5">See plans</button>
    </div>
  );
};

export default function FamilyWeek() {
  const [access, setAccess] = useState(null);
  const [d, setD] = useState(null);
  const [cards, setCards] = useState([]);
  const load = useCallback(async () => {
    try {
      const a = (await api.get("/family-week/access")).data;
      setAccess(a);
      if (!a.family_plan) return;
      const [dash, gal] = await Promise.all([api.get("/family-week"), api.get("/family-week/gallery")]);
      setD(dash.data); setCards(gal.data.cards);
    } catch (e) { setAccess({ missing: true }); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (url, body, ok) => {
    try { await api.post(url, body); if (ok) toast.success(ok); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  if (!access) return <div className="p-8 text-tc-muted font-mono text-sm">Loading your family…</div>;
  if (access.missing) return <div className="p-8 text-tc-sub" data-testid="family-week-not-found">Not found.</div>;
  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-6xl mx-auto space-y-6" data-testid="family-week-page">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-widest text-tc-coach flex items-center gap-1.5"><Users className="w-3.5 h-3.5" /> Family Week</div>
        <h1 className="font-heading text-3xl sm:text-4xl font-bold mt-1">Training together this week</h1>
        <p className="text-sm text-tc-sub mt-2 max-w-2xl">One shared goal for the whole family. Everyone keeps their own XP; we just add it up to see the family's week. Private to your family.</p>
      </div>
      {!access.family_plan ? <Upgrade copy={access.upgrade} /> : !d ? null : (
        <>
          <GoalCard d={d} />
          <GoalPicker d={d} act={act} />
          {d.suggestion && d.is_parent && (
            <div className="tc-card p-4 flex flex-wrap items-center justify-between gap-3" data-testid="family-suggestion">
              <div className="text-sm"><span className="font-semibold">{d.suggestion.by}</span> suggested <span className="font-semibold">{d.suggestion.label}</span></div>
              <div className="flex flex-wrap gap-2">
                {!d.goal.locked && <button data-testid="suggestion-confirm-current" onClick={() => act("/family-week/suggestion", { action: "confirm", week: "current" }, "Goal updated")} className="rounded-lg bg-tc-coach text-tc-bg font-semibold px-3 py-1.5 text-xs">Use this week</button>}
                <button data-testid="suggestion-confirm-next" onClick={() => act("/family-week/suggestion", { action: "confirm", week: "next" }, "Set for next week")} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs">Use next week</button>
                <button data-testid="suggestion-dismiss" onClick={() => act("/family-week/suggestion", { action: "dismiss" })} className="rounded-lg border border-white/15 px-3 py-1.5 text-xs text-tc-muted">Dismiss</button>
              </div>
            </div>
          )}
          <MemberRows members={d.members} />
          <HeadToHead d={d} act={act} />
          <section className="space-y-2" data-testid="family-history">
            <h2 className="text-[11px] font-mono uppercase tracking-widest text-tc-muted flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Past weeks</h2>
            {d.history.length === 0 ? <p className="text-sm text-tc-muted" data-testid="family-history-empty">Your first finished week will show up here.</p> : (
              <div className="flex gap-3 overflow-x-auto pb-1">
                {d.history.map((h) => (
                  <div key={h.week} className="tc-card p-3 min-w-[180px]" data-testid={`family-history-${h.week}`}>
                    <div className="text-[11px] font-mono text-tc-muted">Week of {weekLabel(h.week)}</div>
                    <div className="text-sm font-semibold mt-1">{h.goal}</div>
                    <div className={`text-xs mt-1 ${h.result === "met" ? "text-tc-bull" : "text-tc-sub"}`}>{h.result === "met" ? "Goal met" : "Not met this time"}</div>
                    <div className="text-xs text-tc-muted mt-1">{h.pooled_total} XP added together</div>
                  </div>
                ))}
              </div>
            )}
          </section>
          <section id="family-gallery" className="space-y-3" data-testid="family-gallery">
            <h2 className="text-[11px] font-mono uppercase tracking-widest text-tc-muted flex items-center gap-1.5"><Layers className="w-3.5 h-3.5" /> Family Call Cards</h2>
            {cards.length === 0 ? <p className="text-sm text-tc-muted" data-testid="family-gallery-empty">No Call Cards in the family yet.</p> : (
              <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                {cards.map((c) => (
                  <div key={c.id} className="space-y-1" data-testid="family-gallery-card">
                    <div className="text-xs text-tc-coach font-semibold" data-testid="family-gallery-owner">{c.is_mine ? "You" : c.owner}</div>
                    <CallCard card={c} readOnly={!c.is_mine} />
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}
